import { useContext, useEffect, useRef, useState, type ReactElement } from "react";
import { Link } from "@-/com.npmjs.expo-router";
import { Pressable, View } from "@-/com.npmjs.react-native";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { restartApp } from "@-/dev.improve.haniverse.lib.app.restart";
import { isDeadlineExceededError } from "@-/dev.improve.haniverse.lib.deadline";
import {
  describeDeterministicQueryError,
  describeFailureForUser,
  describeTransientNetworkError,
  isTransientNetworkError,
} from "@-/dev.improve.haniverse.lib.error-classification";
import { captureSentryException } from "@-/dev.improve.haniverse.lib.sentry";
import { QueryClientContext } from "@-/com.npmjs.tanstack__react-query";
import {
  hasAutomaticErrorRecoveryAttemptsLeft,
  recordAutomaticErrorRecoveryAttempt,
  resetAutomaticErrorRecoveryAttempts,
} from "#automatic-error-recovery-attempts";

/**
 * How long to wait before the one automatic recovery attempt.
 *
 * Long enough for a backgrounded app to come back to the foreground (the common cause of the iOS
 * fetch cancellation this recovers from), short enough that the user reads it as a slow load rather
 * than a failure. During this window the LOADING ui stays on screen — the user never learns an
 * error happened at all, which is the point.
 */
const AUTOMATIC_RECOVERY_DELAY_MS = 400;

/**
 * Fires a bounded number of automatic recovery passes for a transient failure.
 *
 * The attempt count deliberately lives OUTSIDE this component (see
 * `automatic-error-recovery-attempts.ts`): `resetError()` unmounts the fallback and the next failure
 * arrives as a brand-new `Error` instance, so any local `useRef`/`useState` — or a guard keyed on
 * error identity — would reset itself on every pass and bound nothing at all.
 */
function useAutomaticErrorRecovery({ error, onRecover }: { error: Error; onRecover: () => void }): {
  isRecovering: boolean;
} {
  // An expired deadline is excluded: the user has ALREADY watched the loading state for the whole
  // bound (a query attempt, or a boundary suspended past `SUSPENSE_DEADLINE_MS`). Silent passes are
  // for the instant-recovery case — a request abandoned by a lifecycle event — and each one here
  // would add another full deadline of skeleton before the user could act.
  const shouldRecover =
    isTransientNetworkError(error) &&
    !isDeadlineExceededError(error) &&
    hasAutomaticErrorRecoveryAttemptsLeft(error);
  const [isRecovering, setIsRecovering] = useState(shouldRecover);
  const hasScheduledRef = useRef(false);

  useEffect(() => {
    if (!shouldRecover || hasScheduledRef.current) {
      return;
    }
    hasScheduledRef.current = true;
    recordAutomaticErrorRecoveryAttempt(error);

    const timeoutId = setTimeout(() => {
      onRecover();
      setIsRecovering(false);
    }, AUTOMATIC_RECOVERY_DELAY_MS);

    return () => {
      clearTimeout(timeoutId);
    };
    // `onRecover` is a fresh closure each render; depending on it would restart the timer on every
    // re-render and defeat the once-per-mount guarantee `hasScheduledRef` provides.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see above
  }, [error, shouldRecover]);

  return { isRecovering };
}

/**
 * The page-level error screen. Two rules govern it, both learned from a real staging failure where
 * a user was shown `fetch failed: FetchRequestCanceledException: Fetch request has been canceled
 * (at Expo/NativeResponse.swift:125)` above a single Retry button:
 *
 *  1. **Never a dead end.** Every path out is present in every build — retry, home, and restart —
 *     and each has a fallback so no button can be a no-op.
 *  2. **Never raw error text as the message.** A native exception with a Swift file and line number
 *     is not something a user can act on. The detail is available behind a disclosure (and in
 *     Sentry), but the message they read is a sentence about what to do next.
 */
export function SuspensePageErrorFallback({
  error,
  loadingFallback,
  logger,
  onLayout,
  resetError,
}: {
  error: Error;
  loadingFallback: ReactElement;
  logger: Console;
  onLayout?: () => void;
  resetError: () => void;
}) {
  // Resolved through context directly rather than via `useQueryClient()`, which THROWS when no
  // provider is above it. A throw inside an error fallback is uncatchable — it would escape past
  // this boundary and take the app down, converting the recoverable failure this screen exists to
  // handle into a crash. In the app a provider is always above us; this costs nothing and removes
  // that whole failure class.
  const queryClient = useContext(QueryClientContext);
  const [isRetrying, setIsRetrying] = useState(false);
  const [areDetailsVisible, setAreDetailsVisible] = useState(__DEV__);

  const transientKind = describeTransientNetworkError(error);
  // One classification decides the copy for this screen AND for the navigation-failure toast, so a
  // schema drift reads as "Haniverse needs an update" everywhere rather than as a different generic
  // failure on each surface. Deterministic kinds are never auto-recovered (see
  // `useAutomaticErrorRecovery`, which is transient-only): retrying the same query cannot change
  // the answer, so the user is told on the first failure instead of after a silent retry budget.
  const failure = describeFailureForUser(error);

  // Clearing the cached rejection is what makes recovery reliable for the WHOLE screen rather than
  // just this boundary. `resetError()` alone already refetches (TanStack's `retryOnMount` defaults
  // to true, so a remounted observer with no data re-issues its query), but the rejected entry stays
  // in the cache — so a sibling boundary observing the same key keeps its error. `resetQueries()`
  // evicts it, which is the same mechanism the sync swap and the offline-mode switch already use.
  //
  // The reset is NOT awaited. `resetQueries()` evicts synchronously, then refetches EVERY active
  // query in the app and resolves only when all of them settle — so awaiting it tied "Trying again…"
  // to the slowest unrelated query anywhere, and one that never settled stranded the button
  // forever. The eviction is all this recovery needs; the remounted page fetches its own data under
  // its own deadlines.
  const recover = () => {
    try {
      queryClient?.resetQueries().catch((resetQueriesError: unknown) => {
        logger.warn("resetQueries refetch failed during error recovery", resetQueriesError);
      });
    } catch (resetQueriesError) {
      // A failed cache reset must not strand the user on the error screen — `resetError()` on its
      // own still refetches, so carry on rather than aborting the recovery.
      logger.warn("resetQueries failed during error recovery", resetQueriesError);
    }
    resetError();
  };

  const { isRecovering } = useAutomaticErrorRecovery({ error, onRecover: recover });

  // Report from an effect, not the render body. A render can run repeatedly and be discarded under
  // concurrent rendering, so logging there both double-logs and can report an error that was never
  // committed. (It also fired on every "Show details" toggle.)
  useEffect(() => {
    logger.error("SuspensePageBoundary caught error", error);
    captureSentryException(error, {
      tags: {
        boundary: "suspense-page",
        phase: "error",
        // Separates "the network hiccuped and we could not recover" from a genuine defect, so the
        // two do not group together in Sentry.
        "suspense.transient": transientKind ?? "none",
        // Groups "the app is out of date for its data" apart from genuine unknowns.
        "suspense.deterministic": describeDeterministicQueryError(error) ?? "none",
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- report once per error, not per render
  }, [error]);

  const handleTryAgain = () => {
    setIsRetrying(true);
    // An explicit ask is a fresh start: give the automatic path its attempts back, so a user who
    // waited out a network outage and pressed the button gets the silent recovery too.
    resetAutomaticErrorRecoveryAttempts();
    recover();
  };

  // While the automatic attempt is in flight, show the loading state — a transient failure that
  // recovers must be completely invisible to the user.
  if (isRecovering) {
    return loadingFallback;
  }

  return (
    <View
      className="flex-1 items-center justify-center gap-3 p-6"
      onLayout={(event) => {
        if (event.nativeEvent.layout.width > 0 && event.nativeEvent.layout.height > 0) {
          onLayout?.();
        }
      }}
    >
      <Text className="text-base font-semibold">{failure.title}</Text>
      <Text className="text-center text-[13px] opacity-60">{failure.body}</Text>

      <Pressable
        className="mt-1 rounded-xs bg-red-500 px-5 py-2"
        disabled={isRetrying}
        onPress={handleTryAgain}
      >
        <Text className="font-semibold text-white">
          {isRetrying ? "Trying again…" : "Try again"}
        </Text>
      </Pressable>

      {/* The escape hatch. `Link` rather than `router.replace` for two reasons: the oxlint
          `no-router-navigation` rule reserves imperative navigation for page-navigator hooks, and a
          navigator re-warms the very queries that just failed — an escape hatch must never
          depend on the thing that broke. `replace` so the broken screen leaves the back
          stack. On native `/` redirects to the learn summary; on web it is the app entry.

          No `onPress` fallback here: `Link`'s `onPress` fires on EVERY press, not just failed ones,
          so pairing it with a restart would reboot the app on a perfectly good navigation. The
          separate "Restart app" button below is the guarantee against an unusable router. */}
      {/* Study data that cannot be opened is not something a retry or the user alone can fix;
          support walks them through recovery. A `Link` for the same reasons as the one below. */}
      {failure.kind === "study-data-unavailable" ? (
        <Link asChild href="/(nav)/support">
          <Pressable
            accessibilityRole="link"
            className="rounded-xs bg-foreground px-5 py-2"
            testID="suspense-page-error-contact-support"
          >
            <Text className="font-semibold text-background">Contact Support</Text>
          </Pressable>
        </Link>
      ) : null}

      <Link asChild href="/" replace>
        <Pressable
          accessibilityLabel="Return to Haniverse home"
          accessibilityRole="link"
          className="rounded-xs border border-foreground/20 px-5 py-2"
        >
          <Text className="font-semibold opacity-70">Return home</Text>
        </Pressable>
      </Link>

      {/* Depends on nothing inside the React tree, so it still works when the router and the query
          client are both unusable. Mirrors the root boundary's final guarantee. */}
      <Pressable
        className="rounded-xs px-5 py-2"
        onPress={() => {
          void restartApp();
        }}
      >
        <Text className="text-[13px] opacity-50">Restart app</Text>
      </Pressable>

      <Pressable
        className="mt-1"
        onPress={() => {
          setAreDetailsVisible((areVisible) => !areVisible);
        }}
      >
        <Text className="text-[12px] underline opacity-40">
          {areDetailsVisible ? "Hide details" : "Show details"}
        </Text>
      </Pressable>
      {areDetailsVisible ? (
        <Text className="text-center font-mono text-[11px] opacity-40" selectable>
          {error.name}: {error.message}
        </Text>
      ) : null}
    </View>
  );
}
